import React from 'react';
import { usePermissions } from '../../hooks/usePermissions';

interface PermissionGuardProps {
  /** Single permission string the user must hold (e.g. "leads:write"). */
  permission?: string;
  /** Array of permissions -- the user needs at least ONE of them. */
  anyPermission?: string[];
  /** Content rendered when the user has the required permission(s). */
  children: React.ReactNode;
  /** Content rendered when the user lacks permission (defaults to null). */
  fallback?: React.ReactNode;
}

/**
 * Conditionally renders its children based on the authenticated user's
 * permissions.  When neither `permission` nor `anyPermission` is provided the
 * children are always rendered (no restriction).
 *
 * Usage:
 * ```tsx
 * <PermissionGuard permission="leads:write">
 *   <Button>Create Lead</Button>
 * </PermissionGuard>
 *
 * <PermissionGuard anyPermission={['leads:write', 'leads:delete']} fallback={<span>No access</span>}>
 *   <DangerZone />
 * </PermissionGuard>
 * ```
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  anyPermission,
  children,
  fallback = null,
}) => {
  const { hasPermission, hasAnyPermission } = usePermissions();

  // When no permission constraint is specified, always render children.
  if (!permission && (!anyPermission || anyPermission.length === 0)) {
    return <>{children}</>;
  }

  // Check single permission
  if (permission && !hasPermission(permission)) {
    return <>{fallback}</>;
  }

  // Check any-of permissions
  if (anyPermission && anyPermission.length > 0 && !hasAnyPermission(anyPermission)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default PermissionGuard;
